import { useEffect, useState } from 'react';
import {
  Button,
  Badge,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Separator,
  Switch,
} from '@rnd-payplus/alloy-react';
import { Trash2, KeyRound, Copy, Check } from 'lucide-react';
import type { Node } from '@xyflow/react';
import { useAppSelector, useAppDispatch } from '../store';
import { fetchConnections } from '../store/slices/connectionSlice';
import { PLATFORM_LABELS } from '../constants/platforms';

interface FlowNodeConfigPanelProps {
  node: Node;
  onConfigChange: (nodeId: string, config: Record<string, unknown>) => void;
  onDelete: (nodeId: string) => void;
}

// Additional platform labels not in the shared constant (payment processors, etc.)
const EXTRA_PLATFORM_LABELS: Record<string, string> = {
  stripe: 'Stripe',
};
const ALL_PLATFORM_LABELS: Record<string, string> = { ...PLATFORM_LABELS, ...EXTRA_PLATFORM_LABELS };

function useConfig(node: Node) {
  return (node.data.config as Record<string, unknown>) || {};
}

// ─── Source: ERP System ───────────────────────────────────────────────────────

function ERPConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const connections = useAppSelector((s) => s.connection.connections);
  const tenantId = useAppSelector((s) => s.auth.user?.tenantId) || 'default';
  const dispatch = useAppDispatch();
  const sapConnections = connections.filter((c) => c.platform === 'sap');
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (connections.length === 0) dispatch(fetchConnections(tenantId));
  }, [connections.length, dispatch, tenantId]);

  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  const endpoint = `${window.location.origin}/api/ingest?flow=${node.id}`;

  const generateKey = async () => {
    setGenerating(true);
    try {
      const res = await fetch('/api/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, name: `SAP ingest (${node.data.label})`, scopes: ['ingest:write'] }),
      });
      const data = await res.json();
      if (data?.fullKey) {
        setGeneratedKey(data.fullKey);
        set('apiKeyId', data.id);
        set('apiKeyPrefix', data.key_prefix);
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyKey = () => {
    if (!generatedKey) return;
    navigator.clipboard.writeText(generatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Connection</Label>
        <Select value={(config.connectionId as string) || ''} onValueChange={(v) => set('connectionId', v)}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Select SAP connection" /></SelectTrigger>
          <SelectContent>
            {sapConnections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            {sapConnections.length === 0 && <SelectItem value="__none" disabled>No SAP connections found</SelectItem>}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Endpoint</Label>
        <Input className="mt-1 font-mono text-[11px]" readOnly value={endpoint} />
        <p className="text-[10px] text-muted-foreground mt-1">Configure SAP to POST payment runs to this URL.</p>
      </div>

      <div>
        <Label className="text-xs">API Key</Label>
        {generatedKey ? (
          <div className="mt-1 space-y-1.5">
            <div className="flex items-center gap-1">
              <Input className="font-mono text-[11px]" readOnly value={generatedKey} />
              <Button variant="outline" size="icon" className="size-9 shrink-0" onClick={copyKey}>
                {copied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
              </Button>
            </div>
            <p className="text-[10px] text-amber-600">Copy now — this key is shown only once.</p>
          </div>
        ) : (
          <Button variant="outline" size="sm" className="mt-1 w-full" onClick={generateKey} disabled={generating}>
            <KeyRound className="mr-2 size-3.5" />
            {generating ? 'Generating…' : 'Generate Key'}
          </Button>
        )}
        {!generatedKey && !!config.apiKeyPrefix && (
          <p className="text-[10px] text-muted-foreground mt-1">Existing key: {String(config.apiKeyPrefix)}…</p>
        )}
      </div>
    </div>
  );
}

// ─── Source: Payment Processor ─────────────────────────────────────────────────

function PaymentProcessorConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const connections = useAppSelector((s) => s.connection.connections);
  const dispatch = useAppDispatch();
  const platforms = ['stripe', 'payplus'];
  const filtered = connections.filter((c) => c.platform === config.platform);

  useEffect(() => {
    if (connections.length === 0) dispatch(fetchConnections('default'));
  }, [connections.length, dispatch]);

  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Platform</Label>
        <Select value={(config.platform as string) || ''} onValueChange={(v) => set('platform', v)}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Select platform" /></SelectTrigger>
          <SelectContent>
            {platforms.map((p) => <SelectItem key={p} value={p}>{ALL_PLATFORM_LABELS[p]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {!!config.platform && (
        <div>
          <Label className="text-xs">Connection</Label>
          <Select value={(config.connectionId as string) || ''} onValueChange={(v) => set('connectionId', v)}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select connection" /></SelectTrigger>
            <SelectContent>
              {filtered.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              {filtered.length === 0 && <SelectItem value="__none" disabled>No connections found</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label className="text-xs">Event Types</Label>
        <Input
          className="mt-1"
          placeholder="payment.completed, refund.created"
          value={(config.eventTypes as string) || ''}
          onChange={(e) => set('eventTypes', e.target.value)}
        />
        <p className="text-[10px] text-muted-foreground mt-1">Comma-separated event types to listen for</p>
      </div>
    </div>
  );
}

// ─── Source: Tungsten Suite ───────────────────────────────────────────────────

function TungstenConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const connections = useAppSelector((s) => s.connection.connections);
  const dispatch = useAppDispatch();
  const products = ['tungsten-ap', 'tungsten-invoice', 'tungsten-network'];
  const filtered = connections.filter((c) => c.platform === config.product);
  const product = config.product as string;

  useEffect(() => {
    if (connections.length === 0) dispatch(fetchConnections('default'));
  }, [connections.length, dispatch]);

  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Product</Label>
        <Select value={product || ''} onValueChange={(v) => set('product', v)}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Select product" /></SelectTrigger>
          <SelectContent>
            {products.map((p) => <SelectItem key={p} value={p}>{ALL_PLATFORM_LABELS[p]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {!!product && (
        <div>
          <Label className="text-xs">Connection</Label>
          <Select value={(config.connectionId as string) || ''} onValueChange={(v) => set('connectionId', v)}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select connection" /></SelectTrigger>
            <SelectContent>
              {filtered.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              {filtered.length === 0 && <SelectItem value="__none" disabled>No connections found</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      )}
      {/* Instance URL — all Tungsten products need this */}
      {!!product && (
        <div>
          <Label className="text-xs">Instance URL</Label>
          <Input
            className="mt-1"
            placeholder={
              product === 'tungsten-invoice' ? 'https://tungsten.acme.com'
                : product === 'tungsten-ap' ? 'https://ap.tungsten.acme.com'
                  : 'https://network.tungsten.acme.com'
            }
            value={(config.instanceUrl as string) || ''}
            onChange={(e) => set('instanceUrl', e.target.value)}
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            {product === 'tungsten-invoice' ? 'Kofax AP API at /kofax-ap/api'
              : product === 'tungsten-ap' ? 'Kofax AP API at /kofax-ap/api/v1'
                : 'Tungsten Network API at /api/v3'}
          </p>
        </div>
      )}
      {/* API Key — shared across all products */}
      {!!product && (
        <div>
          <Label className="text-xs">API Key</Label>
          <Input
            className="mt-1"
            type="password"
            placeholder="X-API-Key"
            value={(config.apiKey as string) || ''}
            onChange={(e) => set('apiKey', e.target.value)}
          />
        </div>
      )}
      {/* Tungsten Invoice — extraction profile config */}
      {product === 'tungsten-invoice' && (
        <>
          <div>
            <Label className="text-xs">Extraction Profile</Label>
            <Select value={(config.extractionProfile as string) || 'ap-standard-v2'} onValueChange={(v) => set('extractionProfile', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ap-standard-v2">AP Standard v2</SelectItem>
                <SelectItem value="ap-standard-v1">AP Standard v1</SelectItem>
                <SelectItem value="custom">Custom Profile</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Data Type</Label>
            <Select value={(config.dataType as string) || 'invoices'} onValueChange={(v) => set('dataType', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="invoices">Invoices</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      {/* Tungsten AP — payment / approval workflow config */}
      {product === 'tungsten-ap' && (
        <>
          <div>
            <Label className="text-xs">Data Type</Label>
            <Select value={(config.dataType as string) || 'payments'} onValueChange={(v) => set('dataType', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="payments">Payments</SelectItem>
                <SelectItem value="pending-approvals">Pending Approvals</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Auto-Approve</Label>
            <Select value={(config.autoApprove as string) || 'disabled'} onValueChange={(v) => set('autoApprove', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="disabled">Disabled</SelectItem>
                <SelectItem value="below-threshold">Below Threshold</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {config.autoApprove === 'below-threshold' && (
            <div>
              <Label className="text-xs">Approval Threshold</Label>
              <Input
                className="mt-1"
                type="number"
                placeholder="10000"
                value={(config.approvalThreshold as string) || ''}
                onChange={(e) => set('approvalThreshold', e.target.value)}
              />
              <p className="text-[10px] text-muted-foreground mt-1">Auto-approve payments below this amount</p>
            </div>
          )}
        </>
      )}
      {/* Tungsten Network — e-invoice config */}
      {product === 'tungsten-network' && (
        <>
          <div>
            <Label className="text-xs">Network ID</Label>
            <Input
              className="mt-1"
              placeholder="Optional network identifier"
              value={(config.networkId as string) || ''}
              onChange={(e) => set('networkId', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">Data Type</Label>
            <Select value={(config.dataType as string) || 'e-invoices'} onValueChange={(v) => set('dataType', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="e-invoices">E-Invoices</SelectItem>
                <SelectItem value="invoices">Invoices</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}
      {/* Status filter — common across products */}
      {!!product && (
        <div>
          <Label className="text-xs">Status Filter</Label>
          <Select value={(config.statusFilter as string) || 'all'} onValueChange={(v) => set('statusFilter', v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              {product === 'tungsten-invoice' && <SelectItem value="processing">Processing</SelectItem>}
              <SelectItem value="completed">Completed</SelectItem>
              {product === 'tungsten-ap' && <SelectItem value="approved">Approved</SelectItem>}
              {product === 'tungsten-network' && <SelectItem value="submitted">Submitted</SelectItem>}
              {product === 'tungsten-network' && <SelectItem value="rejected">Rejected</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      )}
      {/* Webhook secret — optional for all products */}
      {!!product && (
        <div>
          <Label className="text-xs">Webhook Secret</Label>
          <Input
            className="mt-1"
            type="password"
            placeholder="Optional HMAC-SHA256 secret"
            value={(config.webhookSecret as string) || ''}
            onChange={(e) => set('webhookSecret', e.target.value)}
          />
          <p className="text-[10px] text-muted-foreground mt-1">Validates X-Tungsten-Signature header on callbacks</p>
        </div>
      )}
    </div>
  );
}

// ─── Source: SFTP ──────────────────────────────────────────────────────────────

function SFTPConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Storage Connection ID</Label>
        <Input
          className="mt-1"
          placeholder="Storage connection UUID"
          value={(config.storageConnectionId as string) || ''}
          onChange={(e) => set('storageConnectionId', e.target.value)}
        />
        <p className="text-[10px] text-muted-foreground mt-1">From Storage Connections page</p>
      </div>
      <div>
        <Label className="text-xs">File Pattern</Label>
        <Input
          className="mt-1"
          placeholder="*.csv, invoice-*.xml"
          value={(config.filePattern as string) || ''}
          onChange={(e) => set('filePattern', e.target.value)}
        />
      </div>
      <div>
        <Label className="text-xs">Import Frequency</Label>
        <Select value={(config.importFrequency as string) || 'every_5_minutes'} onValueChange={(v) => set('importFrequency', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="every_minute">Every Minute</SelectItem>
            <SelectItem value="every_5_minutes">Every 5 Minutes</SelectItem>
            <SelectItem value="hourly">Hourly</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── Source: File Upload ──────────────────────────────────────────────────────

function FileUploadConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Accepted Formats</Label>
        <Input
          className="mt-1"
          placeholder="CSV, XML, XLSX, PDF, EDI"
          value={(config.acceptedFormats as string) || ''}
          onChange={(e) => set('acceptedFormats', e.target.value)}
        />
        <p className="text-[10px] text-muted-foreground mt-1">Comma-separated file extensions</p>
      </div>
      <div>
        <Label className="text-xs">Max File Size (MB)</Label>
        <Input
          className="mt-1"
          type="number"
          placeholder="50"
          value={(config.maxFileSizeMb as string) || ''}
          onChange={(e) => set('maxFileSizeMb', e.target.value)}
        />
      </div>
      <div>
        <Label className="text-xs">Parser Type</Label>
        <Select value={(config.parserType as string) || 'auto-detect'} onValueChange={(v) => set('parserType', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto-detect">Auto-Detect</SelectItem>
            <SelectItem value="csv">CSV</SelectItem>
            <SelectItem value="sap-f110">SAP F110</SelectItem>
            <SelectItem value="sap-f111">SAP F111</SelectItem>
            <SelectItem value="cxml">cXML</SelectItem>
            <SelectItem value="edi-x12">EDI X12</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── Source: Webhook ──────────────────────────────────────────────────────────

function WebhookConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Webhook Path</Label>
        <Input
          className="mt-1 font-mono text-xs"
          readOnly
          value={`/hooks/${node.id}`}
        />
        <p className="text-[10px] text-muted-foreground mt-1">Auto-generated, read-only</p>
      </div>
      <div>
        <Label className="text-xs">Auth Type</Label>
        <Select value={(config.authType as string) || 'none'} onValueChange={(v) => set('authType', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="api-key">API Key</SelectItem>
            <SelectItem value="hmac-sha256">HMAC-SHA256</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {!!config.authType && config.authType !== 'none' && (
        <div>
          <Label className="text-xs">Secret</Label>
          <Input
            className="mt-1"
            type="password"
            placeholder="Webhook secret"
            value={(config.secret as string) || ''}
            onChange={(e) => set('secret', e.target.value)}
          />
        </div>
      )}
      <div>
        <Label className="text-xs">Content Type Filter</Label>
        <Select value={(config.contentType as string) || 'application/json'} onValueChange={(v) => set('contentType', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="application/json">application/json</SelectItem>
            <SelectItem value="application/xml">application/xml</SelectItem>
            <SelectItem value="text/csv">text/csv</SelectItem>
            <SelectItem value="*/*">Any</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── Transform: Field Mapper ──────────────────────────────────────────────────

function FieldMapperConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const [mappings, setMappings] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/mappings')
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setMappings(data.map((m: any) => ({ id: m.id, name: m.name || `${m.source_format} → ${m.target_format}` })));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Mapping</Label>
        <Select value={(config.mappingId as string) || ''} onValueChange={(v) => set('mappingId', v)}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={loading ? 'Loading…' : 'Select a mapping'} />
          </SelectTrigger>
          <SelectContent>
            {mappings.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            {!loading && mappings.length === 0 && (
              <SelectItem value="__none" disabled>No mappings found</SelectItem>
            )}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground mt-1">
          Manage mappings in the Mappings page.
        </p>
      </div>
    </div>
  );
}

// ─── Destination: Pay+ ────────────────────────────────────────────────────────

function PayPlusConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  const options: { key: string; label: string; hint: string }[] = [
    { key: 'atp', label: 'ATP', hint: 'Auto-to-Pay' },
    { key: 'stp', label: 'STP', hint: 'Straight-through processing' },
    { key: 'createDraftPayRun', label: 'Create Draft Pay Run', hint: 'Hold as draft before release' },
  ];

  return (
    <div className="space-y-3">
      {options.map((opt) => (
        <div key={opt.key} className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Label className="text-xs">{opt.label}</Label>
            <p className="text-[10px] text-muted-foreground">{opt.hint}</p>
          </div>
          <Switch
            checked={!!config[opt.key]}
            onCheckedChange={(v) => set(opt.key, v)}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Destination: SAP ERP ────────────────────────────────────────────────────

function SAPDestConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const connections = useAppSelector((s) => s.connection.connections);
  const dispatch = useAppDispatch();
  const filtered = connections.filter((c) => c.platform === 'sap');

  useEffect(() => {
    if (connections.length === 0) dispatch(fetchConnections('default'));
  }, [connections.length, dispatch]);

  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Connection</Label>
        <Select value={(config.connectionId as string) || ''} onValueChange={(v) => set('connectionId', v)}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Select SAP connection" /></SelectTrigger>
          <SelectContent>
            {filtered.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            {filtered.length === 0 && <SelectItem value="__none" disabled>No SAP connections found</SelectItem>}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">SAP Base URL</Label>
        <Input
          className="mt-1"
          placeholder="https://tenant.cfapps.eu10.hana.ondemand.com"
          value={(config.baseUrl as string) || ''}
          onChange={(e) => set('baseUrl', e.target.value)}
        />
      </div>
      <div>
        <Label className="text-xs">Company Code</Label>
        <Input
          className="mt-1"
          placeholder="0001"
          value={(config.companyCode as string) || ''}
          onChange={(e) => set('companyCode', e.target.value)}
        />
      </div>
      <div>
        <Label className="text-xs">Delivery Method</Label>
        <Select value={(config.deliveryMethod as string) || 'odata'} onValueChange={(v) => set('deliveryMethod', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="odata">OData API (POST)</SelectItem>
            <SelectItem value="f110">F110 File (Batch Processor)</SelectItem>
            <SelectItem value="f111">F111 File (Batch Processor)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Retry on Failure</Label>
        <Switch checked={!!config.retryEnabled} onCheckedChange={(v) => set('retryEnabled', v)} />
      </div>
      {!!config.retryEnabled && (
        <div>
          <Label className="text-xs">Max Retries</Label>
          <Input className="mt-1" type="number" placeholder="3" value={(config.maxRetries as string) || ''} onChange={(e) => set('maxRetries', e.target.value)} />
        </div>
      )}
    </div>
  );
}

// ─── Destination: Tungsten Invoice ──────────────────────────────────────────

function TungstenInvoiceDestConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Instance URL</Label>
        <Input className="mt-1" placeholder="https://tungsten.acme.com" value={(config.instanceUrl as string) || ''} onChange={(e) => set('instanceUrl', e.target.value)} />
        <p className="text-[10px] text-muted-foreground mt-1">Kofax AP API at /kofax-ap/api</p>
      </div>
      <div>
        <Label className="text-xs">API Key</Label>
        <Input className="mt-1" type="password" placeholder="X-API-Key" value={(config.apiKey as string) || ''} onChange={(e) => set('apiKey', e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Extraction Profile</Label>
        <Select value={(config.extractionProfile as string) || 'ap-standard-v2'} onValueChange={(v) => set('extractionProfile', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ap-standard-v2">AP Standard v2</SelectItem>
            <SelectItem value="ap-standard-v1">AP Standard v1</SelectItem>
            <SelectItem value="custom">Custom Profile</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Auto-Process on Delivery</Label>
        <Switch checked={config.autoProcess !== false} onCheckedChange={(v) => set('autoProcess', v)} />
      </div>
    </div>
  );
}

// ─── Destination: Tungsten AP ───────────────────────────────────────────────

function TungstenAPDestConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Instance URL</Label>
        <Input className="mt-1" placeholder="https://ap.tungsten.acme.com" value={(config.instanceUrl as string) || ''} onChange={(e) => set('instanceUrl', e.target.value)} />
        <p className="text-[10px] text-muted-foreground mt-1">Kofax AP API at /kofax-ap/api/v1</p>
      </div>
      <div>
        <Label className="text-xs">API Key</Label>
        <Input className="mt-1" type="password" placeholder="X-API-Key" value={(config.apiKey as string) || ''} onChange={(e) => set('apiKey', e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Payment Action</Label>
        <Select value={(config.paymentAction as string) || 'create'} onValueChange={(v) => set('paymentAction', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="create">Create Payment</SelectItem>
            <SelectItem value="approve">Auto-Approve</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {config.paymentAction === 'approve' && (
        <div>
          <Label className="text-xs">Approval Threshold</Label>
          <Input className="mt-1" type="number" placeholder="10000" value={(config.approvalThreshold as string) || ''} onChange={(e) => set('approvalThreshold', e.target.value)} />
          <p className="text-[10px] text-muted-foreground mt-1">Auto-approve payments below this amount</p>
        </div>
      )}
    </div>
  );
}

// ─── Destination: Tungsten Network ──────────────────────────────────────────

function TungstenNetworkDestConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Instance URL</Label>
        <Input className="mt-1" placeholder="https://network.tungsten.acme.com" value={(config.instanceUrl as string) || ''} onChange={(e) => set('instanceUrl', e.target.value)} />
        <p className="text-[10px] text-muted-foreground mt-1">Tungsten Network API at /api/v3</p>
      </div>
      <div>
        <Label className="text-xs">API Key</Label>
        <Input className="mt-1" type="password" placeholder="X-API-Key" value={(config.apiKey as string) || ''} onChange={(e) => set('apiKey', e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Network ID</Label>
        <Input className="mt-1" placeholder="Optional network identifier" value={(config.networkId as string) || ''} onChange={(e) => set('networkId', e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Submission Type</Label>
        <Select value={(config.submissionType as string) || 'e-invoice'} onValueChange={(v) => set('submissionType', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="e-invoice">E-Invoice</SelectItem>
            <SelectItem value="invoice">Standard Invoice</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── Destination: Tungsten Pay ──────────────────────────────────────────────

function TungstenPayDestConfig({ node, onConfigChange }: Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>) {
  const config = useConfig(node);
  const set = (key: string, value: unknown) => onConfigChange(node.id, { ...config, [key]: value });

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">API Key</Label>
        <Input className="mt-1" type="password" placeholder="X-API-Key" value={(config.apiKey as string) || ''} onChange={(e) => set('apiKey', e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Base URL</Label>
        <Input className="mt-1" placeholder="https://api.tungstenpay.com" value={(config.baseUrl as string) || 'https://api.tungstenpay.com'} onChange={(e) => set('baseUrl', e.target.value)} />
      </div>
      <div>
        <Label className="text-xs">Payment Reference Prefix</Label>
        <Input className="mt-1" placeholder="PAY-" value={(config.referencePrefix as string) || ''} onChange={(e) => set('referencePrefix', e.target.value)} />
        <p className="text-[10px] text-muted-foreground mt-1">Prefix added to payment references for traceability</p>
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">Retry on Failure</Label>
        <Switch checked={!!config.retryEnabled} onCheckedChange={(v) => set('retryEnabled', v)} />
      </div>
      {!!config.retryEnabled && (
        <div>
          <Label className="text-xs">Max Retries</Label>
          <Input className="mt-1" type="number" placeholder="3" value={(config.maxRetries as string) || ''} onChange={(e) => set('maxRetries', e.target.value)} />
        </div>
      )}
    </div>
  );
}

// ─── Config Panel Router ──────────────────────────────────────────────────────

const CONFIG_COMPONENTS: Record<string, React.FC<Pick<FlowNodeConfigPanelProps, 'node' | 'onConfigChange'>>> = {
  'source.erp': ERPConfig,
  'source.payment-processor': PaymentProcessorConfig,
  'source.tungsten-invoice': TungstenConfig,
  'source.tungsten-network': TungstenConfig,
  'source.tungsten-ap': TungstenConfig,
  'source.sftp': SFTPConfig,
  'source.file-upload': FileUploadConfig,
  'source.webhook': WebhookConfig,
  'transform.field-mapper': FieldMapperConfig,
  'destination.payplus': PayPlusConfig,
  'destination.tungsten-invoice': TungstenInvoiceDestConfig,
  'destination.tungsten-ap': TungstenAPDestConfig,
  'destination.tungsten-network': TungstenNetworkDestConfig,
};

export default function FlowNodeConfigPanel({ node, onConfigChange, onDelete }: FlowNodeConfigPanelProps) {
  const nodeTypeId = node.data.nodeTypeId as string;
  const ConfigComponent = CONFIG_COMPONENTS[nodeTypeId];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{node.data.label as string}</h3>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => onDelete(node.id)}>
          <Trash2 className="size-3.5" />
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{node.data.description as string}</p>
      <Badge variant="outline">{node.data.category as string}</Badge>
      <Separator />
      {ConfigComponent ? (
        <ConfigComponent node={node} onConfigChange={onConfigChange} />
      ) : (
        <p className="text-xs text-muted-foreground">No configuration available for this node type.</p>
      )}
    </div>
  );
}
